type Point3 = [number, number, number];

type ProjectedPolygonArea = {
    area: number;
    centroid: Point3;
    normal: Point3;
};

const EPSILON = 1e-8;

const subtract = (a: Point3, b: Point3): Point3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];

const addScaled = (a: Point3, b: Point3, scalar: number): Point3 => [
    a[0] + b[0] * scalar,
    a[1] + b[1] * scalar,
    a[2] + b[2] * scalar
];

const dot = (a: Point3, b: Point3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

const cross = (a: Point3, b: Point3): Point3 => [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0]
];

const length = (v: Point3) => Math.sqrt(dot(v, v));

const normalize = (v: Point3): Point3 | null => {
    const len = length(v);
    if (len <= EPSILON) {
        return null;
    }
    return [v[0] / len, v[1] / len, v[2] / len];
};

const averagePoint = (points: Point3[]): Point3 => {
    const sum: Point3 = [0, 0, 0];
    for (const point of points) {
        sum[0] += point[0];
        sum[1] += point[1];
        sum[2] += point[2];
    }

    const inv = 1 / points.length;
    return [sum[0] * inv, sum[1] * inv, sum[2] * inv];
};

const chooseReferenceAxis = (normal: Point3): Point3 => {
    const absX = Math.abs(normal[0]);
    const absY = Math.abs(normal[1]);
    const absZ = Math.abs(normal[2]);

    if (absX <= absY && absX <= absZ) {
        return [1, 0, 0];
    }
    if (absY <= absX && absY <= absZ) {
        return [0, 1, 0];
    }
    return [0, 0, 1];
};

const computeNewellNormal = (points: Point3[]): Point3 | null => {
    let nx = 0;
    let ny = 0;
    let nz = 0;

    for (let i = 0; i < points.length; i++) {
        const current = points[i];
        const next = points[(i + 1) % points.length];

        nx += (current[1] - next[1]) * (current[2] + next[2]);
        ny += (current[2] - next[2]) * (current[0] + next[0]);
        nz += (current[0] - next[0]) * (current[1] + next[1]);
    }

    return normalize([nx, ny, nz]);
};

const computeFallbackNormal = (points: Point3[]): Point3 | null => {
    const origin = points[0];
    for (let i = 1; i < points.length - 1; i++) {
        const e0 = subtract(points[i], origin);
        const e1 = subtract(points[i + 1], origin);
        const n = normalize(cross(e0, e1));
        if (n) {
            return n;
        }
    }
    return null;
};

const computeProjectedPolygonArea = (points: Point3[]): ProjectedPolygonArea | null => {
    if (!Array.isArray(points) || points.length < 3) {
        return null;
    }

    // Assumes points represent a planar polygon in world space.
    // We compute area by projecting onto a local 2D basis defined by the polygon normal.

    const centroidOrigin = averagePoint(points);
    const normal = computeNewellNormal(points) ?? computeFallbackNormal(points);
    if (!normal) {
        return null;
    }

    const axisU = normalize(cross(chooseReferenceAxis(normal), normal));
    if (!axisU) {
        return null;
    }
    const axisV = cross(normal, axisU);

    const projected = points.map((point) => {
        const delta = subtract(point, centroidOrigin);
        return {
            x: dot(delta, axisU),
            y: dot(delta, axisV)
        };
    });

    let crossSum = 0;
    let centroidXSum = 0;
    let centroidYSum = 0;

    for (let i = 0; i < projected.length; i++) {
        const current = projected[i];
        const next = projected[(i + 1) % projected.length];
        const crossTerm = current.x * next.y - next.x * current.y;
        crossSum += crossTerm;
        centroidXSum += (current.x + next.x) * crossTerm;
        centroidYSum += (current.y + next.y) * crossTerm;
    }

    const signedArea = crossSum * 0.5;
    const area = Math.abs(signedArea);

    if (area <= EPSILON) {
        return {
            area: 0,
            centroid: centroidOrigin,
            normal
        };
    }

    const factor = 1 / (6 * signedArea);
    const centroid2dX = centroidXSum * factor;
    const centroid2dY = centroidYSum * factor;

    let centroid = centroidOrigin;
    centroid = addScaled(centroid, axisU, centroid2dX);
    centroid = addScaled(centroid, axisV, centroid2dY);

    return {
        area,
        centroid,
        normal
    };
};

export { computeProjectedPolygonArea };
export type { Point3, ProjectedPolygonArea };
